<template>
    <div v-if="safe" :class="['cash-drawer-card', { safeMode }]">
        <div>
            <router-link :to="`/safe-drawers/${safe.id}`">
                <div class="d-flex" style="gap: 10px">
                    <i v-if="safeMode" class="drawer-card-icon fas fa-vault"></i>
                    <i v-else class="drawer-card-icon fas fa-wallet"></i>
                    <div>
                        <div class="safe-name">{{ safe.name }}</div>
                        <p class="text-secondary">{{ safe.description }}</p>
                    </div>
                </div>
            </router-link>
        </div>
        <div class="text-end">
            <div class="amount-text">${{ displayAmount }}</div>
            <div
                :class="[{ 'success-chip': safe.isOpen }, { 'fail-chip': !safe.isOpen }]"
                v-if="safeMode"
            >
                {{ safe.isOpen ? "Open" : "Closed" }}
            </div>
            <div :class="[{ 'success-chip': safe.isOpen }, { 'fail-chip': !safe.isOpen }]" v-else>
                {{ safe.isOpen ? "Cashed In" : "Cashed Out" }}
            </div>
            <div v-if="lastCashOutDisplay" class="snapshot-meta">
                {{ lastCashOutDisplay }}
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        safe: { type: Object, default: null },
        safeMode: { type: Boolean, default: false },
        // For virtual safes: pass child drawers to calculate accurate total
        childDrawers: { type: Array, default: () => [] },
        // When provided, this value represents "Available in Safe" (sum of SafeDrawerAmount)
        // and takes precedence for safeMode display
        availableAmount: { type: Number, default: null },
    },
    computed: {
        /**
         * COVA-STYLE: Calculate the display amount for the safe/drawer.
         * 
         * For safes (parent containers):
         * - Display "Total On Premises" = Safe On Hand + Drawers On Hand
         * - Safe On Hand: cash physically in the safe (availableAmount prop)
         * - Drawers On Hand: sum of totalCashAmount from all child drawers
         * 
         * For drawers (child containers):
         * - Display the drawer's current cash and non-cash balances
         */
        displayAmount() {
            // For SAFES: Calculate Total On Premises
            if (this.safeMode) {
                // Safe On Hand: cash in the parent safe itself (from availableAmount prop)
                const safeOnHand = Number(this.availableAmount || 0)

                // Drawers On Hand: sum of cash in all child drawers
                let drawersOnHand = 0
                if (this.childDrawers && this.childDrawers.length > 0) {
                    drawersOnHand = this.childDrawers.reduce((sum, drawer) => {
                        return sum + Number(drawer.totalCashAmount || 0)
                }, 0)
                }
                
                // Total On Premises = Safe + Drawers
                const totalOnPremises = safeOnHand + drawersOnHand
                return totalOnPremises.toFixed(2)
            }

            // For DRAWERS: Prefer the authoritative balance breakdown. Keep the
            // aggregate field only as a fallback for older API payloads.
            const hasCashBalance =
                this.safe.totalCashAmount !== undefined &&
                this.safe.totalCashAmount !== null
            const hasNonCashBalance =
                this.safe.totalNonCashAmount !== undefined &&
                this.safe.totalNonCashAmount !== null
            const hasBalanceBreakdown = hasCashBalance && hasNonCashBalance
            const currentBalance = hasBalanceBreakdown
                ? Number(this.safe.totalCashAmount || 0) +
                  Number(this.safe.totalNonCashAmount || 0)
                : Number(this.safe.totalAmount || 0)
            return currentBalance.toFixed(2)
        },
        lastCashOutDisplay() {
            const snapshot = this.safe && this.safe.lastCashOutSnapshot
            if (!snapshot) {
                return null
            }
            const total = Number(snapshot.totalAmount || 0).toFixed(2)
            const date = snapshot.createdAtFormattedDateOnly || snapshot.createdAtFormatted1
            return date ? `Last cash-out: $${total} on ${date}` : `Last cash-out: $${total}`
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.cash-drawer-card {
    display: flex;
    justify-content: space-between;
    margin-top: 15px;
    margin-bottom: 25px;
}
.drawer-card-icon {
    color: $primaryColor;
    font-size: 50px;
}
.safe-name {
    font-size: 1.2em;
    font-weight: 500;
}
.amount-text {
    font-size: 1.5em;
}
.safeMode {
    .amount-text {
        font-size: 2em;
    }
}
.success-chip,
.fail-chip {
    padding: 2px 5px;
    border-radius: 10px;
    font-size: 0.9em;
    text-align: center;
    background-color: #ddd;
}
.success-chip {
    color: white;
    background-color: rgb(17, 180, 17);
}
.snapshot-meta {
    margin-top: 6px;
    font-size: 0.85em;
    color: #6c757d;
}
</style>
